/**
 * User API client — wraps every authenticated `/api/user/*` endpoint.
 *
 * Mirrors api/src/routes/user.js. Every call attaches the access token
 * from `useAuthStore` as a Bearer header. The shared `api` wrapper
 * already unwraps the REST-SPEC envelope's `data` field, so callers
 * receive the inner payload directly (e.g. `{ alerts: [...] }`).
 *
 * Types intentionally stay loose around `criteria` / `filters` /
 * `consent` because the underlying schemas (spec §10.7, §13.4) are
 * defined in the API and we don't want the web client to drift; the
 * forms validate via Zod before submission instead.
 */

import { api } from './api';
import { useAuthStore } from '../stores/authStore';

function authHeaders(): Record<string, string> {
  const token = useAuthStore.getState().token;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

// ── Types ──────────────────────────────────────────────────────────────────

export type AlertChannel = 'email' | 'in_app';
export type AlertFrequency = 'immediate' | 'daily' | 'weekly';
export type AlertStockStatus = 'in_stock' | 'out_of_stock' | 'limited' | 'unknown';

export interface AlertQuietHours {
  enabled: boolean;
  start?: string;
  end?: string;
  timezone?: string;
}

export interface AlertCriteria {
  caliber?: string[];
  brand?: string[];
  productLine?: string[];
  bulletWeightGrains?: number[];
  bulletType?: string[];
  caseMaterial?: string[];
  maxPricePerRound?: number | null;
  maxTotalPrice?: number | null;
  minRoundCount?: number | null;
  stockStatus?: AlertStockStatus[];
  excludeReloaded?: boolean;
  excludedRetailers?: string[];
  includedRetailers?: string[];
  useCases?: string[];
  lotNumbers?: string[];
}

export interface UserAlert {
  id: string;
  userId: string;
  name: string;
  criteria: AlertCriteria;
  channels: AlertChannel[];
  frequency: AlertFrequency;
  quietHours: AlertQuietHours | null;
  maxPerDay: number | null;
  isActive: boolean;
  lastFiredAt: string | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface AlertCreateBody {
  name: string;
  criteria: AlertCriteria;
  channels?: AlertChannel[];
  frequency?: AlertFrequency;
  quietHours?: AlertQuietHours | null;
  maxPerDay?: number | null;
  isActive?: boolean;
}

export type AlertPatchBody = Partial<AlertCreateBody>;

export interface SavedSearch {
  id: string;
  userId: string;
  name: string;
  query: string | null;
  filters: Record<string, unknown> | null;
  criteria: AlertCriteria | null;
  isAlertBacked: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface SavedSearchCreateBody {
  name: string;
  query?: string | null;
  filters?: Record<string, unknown> | null;
  criteria?: AlertCriteria | null;
  isAlertBacked?: boolean;
}

export interface WatchlistListingSummary {
  id: string;
  title: string | null;
  sourceUrl: string | null;
  price: number | null;
  pricePerRound: number | null;
  roundCount: number | null;
  stockStatus: AlertStockStatus;
  dataFreshness: 'fresh' | 'aging' | 'stale' | 'unavailable';
  lastUpdated?: string | null;
  retailer?: { id: string | null; name: string | null; slug?: string | null } | null;
  attributes?: Record<string, unknown> | null;
}

export interface WatchlistItem {
  id: string;
  userId: string;
  productListingId: string;
  notes: string | null;
  createdAt?: string;
  listing?: WatchlistListingSummary | null;
}

export interface WatchlistAddBody {
  productListingId: string;
  notes?: string;
}

export interface FavoriteRetailerSummary {
  id: string;
  name: string | null;
  slug: string | null;
  logoUrl: string | null;
}

export interface FavoriteRetailer {
  id: string;
  userId: string;
  retailerId: string;
  createdAt?: string;
  retailer?: FavoriteRetailerSummary | null;
}

export interface HiddenRetailer {
  id: string;
  userId: string;
  retailerId: string;
  createdAt?: string;
  retailer?: FavoriteRetailerSummary | null;
}

export interface RetailerRating {
  id: string;
  userId: string;
  retailerId: string;
  rating: number;
  comment: string | null;
  createdAt?: string;
  updatedAt?: string;
  retailer?: FavoriteRetailerSummary | null;
}

export interface RetailerRatingUpsertBody {
  retailerId: string;
  rating: number;
  comment?: string | null;
}

export type NotificationChannel = 'email' | 'in_app';
export type NotificationStatus = 'pending' | 'sent' | 'failed' | 'read';

export interface UserNotification {
  id: string;
  userId: string;
  alertEventId: string | null;
  channel: NotificationChannel;
  subject: string | null;
  body: string | null;
  status: NotificationStatus;
  sentAt: string | null;
  readAt: string | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface ConsentPreferences {
  strictlyNecessary: boolean;
  analytics: boolean;
  personalization: boolean;
  behavioralAdvertising: boolean;
  advertiserDataSharing: boolean;
  marketingEmail: boolean;
  consentedAt?: string | null;
  updatedAt?: string | null;
  /**
   * Coarse ISO 3166 country (or subdivision) code derived at signup from a
   * geo/CDN header. Present when the region could be resolved; null otherwise.
   * Used only to explain why defaults were set as they were — never for
   * enforcement. See docs/design/geo-privacy.md.
   */
  region?: string | null;
}

export type ConsentPatchBody = Partial<
  Pick<
    ConsentPreferences,
    | 'analytics'
    | 'personalization'
    | 'behavioralAdvertising'
    | 'advertiserDataSharing'
    | 'marketingEmail'
  >
>;

export interface ExportPayload {
  user: Record<string, unknown>;
  alerts: UserAlert[];
  savedSearches: SavedSearch[];
  watchlist: WatchlistItem[];
  favoriteRetailers: FavoriteRetailer[];
  hiddenRetailers: HiddenRetailer[];
  retailerRatings: RetailerRating[];
  consent: ConsentPreferences;
  exportedAt: string;
}

export interface DeleteAccountResult {
  deleted: boolean;
  scheduledPurgeAt?: string | null;
}

// ── Endpoints ──────────────────────────────────────────────────────────────

export const userApi = {
  // Alerts ----------------------------------------------------------------
  listAlerts(filters: { isActive?: boolean } = {}): Promise<{ alerts: UserAlert[] }> {
    const query =
      filters.isActive === undefined ? '' : `?isActive=${filters.isActive ? 'true' : 'false'}`;
    return api.get<{ alerts: UserAlert[] }>(`/user/alerts${query}`, { headers: authHeaders() });
  },
  createAlert(body: AlertCreateBody): Promise<{ alert: UserAlert }> {
    return api.post<{ alert: UserAlert }>('/user/alerts', body, { headers: authHeaders() });
  },
  updateAlert(id: string, patch: AlertPatchBody): Promise<{ alert: UserAlert }> {
    return api.patch<{ alert: UserAlert }>(`/user/alerts/${id}`, patch, { headers: authHeaders() });
  },
  deleteAlert(id: string): Promise<{ deleted: boolean }> {
    return api.delete<{ deleted: boolean }>(`/user/alerts/${id}`, { headers: authHeaders() });
  },

  // Saved searches --------------------------------------------------------
  listSavedSearches(): Promise<{ savedSearches: SavedSearch[] }> {
    return api.get<{ savedSearches: SavedSearch[] }>('/user/saved-searches', {
      headers: authHeaders(),
    });
  },
  createSavedSearch(body: SavedSearchCreateBody): Promise<{ savedSearch: SavedSearch }> {
    return api.post<{ savedSearch: SavedSearch }>('/user/saved-searches', body, {
      headers: authHeaders(),
    });
  },
  deleteSavedSearch(id: string): Promise<{ deleted: boolean }> {
    return api.delete<{ deleted: boolean }>(`/user/saved-searches/${id}`, {
      headers: authHeaders(),
    });
  },

  // Watchlist -------------------------------------------------------------
  listWatchlist(): Promise<{ watchlist: WatchlistItem[] }> {
    return api.get<{ watchlist: WatchlistItem[] }>('/user/watchlist', { headers: authHeaders() });
  },
  addWatchlist(body: WatchlistAddBody): Promise<{ item: WatchlistItem }> {
    return api.post<{ item: WatchlistItem }>('/user/watchlist', body, { headers: authHeaders() });
  },
  removeWatchlist(id: string): Promise<{ deleted: boolean }> {
    return api.delete<{ deleted: boolean }>(`/user/watchlist/${id}`, { headers: authHeaders() });
  },

  // Favorite retailers ----------------------------------------------------
  listFavoriteRetailers(): Promise<{ favorites: FavoriteRetailer[] }> {
    return api.get<{ favorites: FavoriteRetailer[] }>('/user/favorite-retailers', {
      headers: authHeaders(),
    });
  },
  addFavoriteRetailer(retailerId: string): Promise<{ favorite: FavoriteRetailer }> {
    return api.post<{ favorite: FavoriteRetailer }>(
      '/user/favorite-retailers',
      { retailerId },
      { headers: authHeaders() }
    );
  },
  removeFavoriteRetailer(id: string): Promise<{ deleted: boolean }> {
    return api.delete<{ deleted: boolean }>(`/user/favorite-retailers/${id}`, {
      headers: authHeaders(),
    });
  },

  // Hidden retailers ------------------------------------------------------
  listHiddenRetailers(): Promise<{ hidden: HiddenRetailer[] }> {
    return api.get<{ hidden: HiddenRetailer[] }>('/user/hidden-retailers', {
      headers: authHeaders(),
    });
  },
  addHiddenRetailer(retailerId: string): Promise<{ hidden: HiddenRetailer }> {
    return api.post<{ hidden: HiddenRetailer }>(
      '/user/hidden-retailers',
      { retailerId },
      { headers: authHeaders() }
    );
  },
  removeHiddenRetailer(id: string): Promise<{ deleted: boolean }> {
    return api.delete<{ deleted: boolean }>(`/user/hidden-retailers/${id}`, {
      headers: authHeaders(),
    });
  },

  // Retailer ratings ------------------------------------------------------
  listRetailerRatings(): Promise<{ ratings: RetailerRating[] }> {
    return api.get<{ ratings: RetailerRating[] }>('/user/retailer-ratings', {
      headers: authHeaders(),
    });
  },
  upsertRetailerRating(body: RetailerRatingUpsertBody): Promise<{ rating: RetailerRating }> {
    return api.put<{ rating: RetailerRating }>('/user/retailer-ratings', body, {
      headers: authHeaders(),
    });
  },
  removeRetailerRating(id: string): Promise<{ deleted: boolean }> {
    return api.delete<{ deleted: boolean }>(`/user/retailer-ratings/${id}`, {
      headers: authHeaders(),
    });
  },

  // Notifications ---------------------------------------------------------
  listNotifications(opts: { limit?: number } = {}): Promise<{ notifications: UserNotification[] }> {
    const query = opts.limit === undefined ? '' : `?limit=${opts.limit}`;
    return api.get<{ notifications: UserNotification[] }>(`/user/notifications${query}`, {
      headers: authHeaders(),
    });
  },
  deleteNotification(id: string): Promise<{ deleted: boolean }> {
    return api.delete<{ deleted: boolean }>(`/user/notifications/${id}`, {
      headers: authHeaders(),
    });
  },

  // Privacy ---------------------------------------------------------------
  getPrivacy(): Promise<{ consent: ConsentPreferences }> {
    return api.get<{ consent: ConsentPreferences }>('/user/privacy', { headers: authHeaders() });
  },
  patchPrivacy(patch: ConsentPatchBody): Promise<{ consent: ConsentPreferences }> {
    return api.patch<{ consent: ConsentPreferences }>('/user/privacy', patch, {
      headers: authHeaders(),
    });
  },

  // Export / delete -------------------------------------------------------
  exportAll(): Promise<ExportPayload> {
    return api.get<ExportPayload>('/user/export', { headers: authHeaders() });
  },
  deleteAccount(reason?: string): Promise<DeleteAccountResult> {
    return api.delete<DeleteAccountResult>('/user/account', {
      headers: {
        ...authHeaders(),
        ...(reason !== undefined ? { 'Content-Type': 'application/json' } : {}),
      },
    });
  },
};

export default userApi;
